import { useApplyCustomerAffiliateCodeMutation } from "Api/Mutations/Affiliate/useApplyCustomerAffiliateCodeMutation";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { useCreateVerificationsMutation } from "Api/Mutations/Verifications/useCreateVerificationsMutation";
import { useUpdateVerificationsMutation } from "Api/Mutations/Verifications/useUpdateVerificationsMutation";
import { AffiliateCodeField } from "Components/Affiliate/AffiliateCodeField/AffiliateCodeField";
import { getAffiliateCodeError } from "Components/Affiliate/AffiliateCodeField/affiliateCodeErrors";
import { useAffiliateReferrer } from "Components/Affiliate/AffiliateCodeField/useAffiliateReferrer";
import { useAffiliateCodeVerification } from "Components/Affiliate/AffiliateCodeField/useAffiliateCodeVerification";
import {
  FinalStepForm,
  type FinalStepFormModel,
} from "Components/Onboarding/NaturalPerson/Steps/FinalStep/FinalStepForm";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { useCurrentUserParty } from "Hooks/useCurrentUserParty";
import type { UserVerificationDto } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { removeLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";

type Props = { userVerificationData: UserVerificationDto };

export const FinalStepContent = ({
  userVerificationData: {
    isPoliticallyExposedPerson,
    hasNonEUResidence,
    hasNonEUNationality,
  },
}: Props) => {
  const { data: partyData, isLoading } = useCurrentUserParty();
  const dispatch = useDispatch();
  const { t } = useResource();
  const { mutate: putParty, isPending: isCreatingParty } = usePartyMutation();
  const { lockedReferrer, isLoading: isReferrerLoading } = useAffiliateReferrer(
    AccessRoleCodes.Customer,
  );
  const affiliateCode = useAffiliateCodeVerification({
    role: AccessRoleCodes.Customer,
    isEnabled: !isReferrerLoading && !lockedReferrer,
  });
  const { mutate: applyAffiliateCode, isPending: isApplyingAffiliateCode } =
    useApplyCustomerAffiliateCodeMutation({
      // The affiliate code is optional — a failure to attach it never blocks the registration. An
      // affiliate that is already attached (the lookup had failed) is the outcome we wanted anyway.
      onError: (error) => {
        if (getAffiliateCodeError(error) !== "AffiliateReferrerAlreadySet") {
          toast.warning(
            t(Resources.Components.Affiliate.CodeField.ApplyFailed),
          );
        }
      },
    });
  const { mutate: postVerifications, isPending: isPostingVerifications } =
    useCreateVerificationsMutation({
      onSuccess: () => {
        // No navigate() here: the user-info refresh is async and the route gates would still see the
        // stale status. CompleteCustomerOnboardingPage redirects once the fresh user and verification
        // arrive — to the dashboard when the account is Full, to the bank connect when only that is missing.
        // The registration is submitted — a code left from an affiliate link has done its job.
        removeLocalStorageItem(LocalStorageKeys.AffiliateCode);
        dispatch(getUserInfoAsync.request(undefined));
        toast.success(t(Resources.Onboarding.Steps.FinalStep.SuccessToast));
      },
    });
  const { mutate: updateVerifications, isPending: isUpdatingVerifications } =
    useUpdateVerificationsMutation({
      onSuccess: () => {
        // update updates the data, but in order to revalidate the verifications on BE, the post needs to be called
        postVerifications();
      },
    });

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  if (!partyData) {
    return (
      <ErrorText>
        {t(Resources.Onboarding.Steps.FinalStep.LoadingError)}
      </ErrorText>
    );
  }

  const onSubmit = ({
    phone,
    isPoliticallyExposedPerson,
    hasNonEUResidence,
    hasNonEUNationality,
  }: FinalStepFormModel) => {
    if (!partyData.publicID) {
      throw new Error("Error: Missing party publicID");
    }

    if (affiliateCode.hasUnverifiedCode) {
      affiliateCode.showUnverifiedError();
      return;
    }

    const partyPublicID = partyData.publicID;
    const submitRegistration = () =>
      putParty(
        { data: { ...partyData, phone }, publicID: partyPublicID },
        {
          onSuccess: () => {
            updateVerifications({
              isPoliticallyExposedPerson,
              hasNonEUResidence,
              hasNonEUNationality,
              uboUnknown: false,
            });
          },
        },
      );

    const { verifiedCode } = affiliateCode;
    if (!verifiedCode) {
      submitRegistration();
      return;
    }

    applyAffiliateCode(verifiedCode, { onSettled: submitRegistration });
  };

  const defaultValues: FinalStepFormModel = {
    phone: partyData.phone,
    isPoliticallyExposedPerson,
    hasNonEUResidence,
    hasNonEUNationality,
  };

  return (
    <FinalStepForm
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      isSubmitting={
        isApplyingAffiliateCode ||
        isCreatingParty ||
        isPostingVerifications ||
        isUpdatingVerifications
      }
      isSubmitDisabled={
        isReferrerLoading || affiliateCode.fieldProps.isVerifying
      }
      affiliateCodeField={
        <AffiliateCodeField
          lockedReferrer={lockedReferrer}
          isLoading={isReferrerLoading}
          {...affiliateCode.fieldProps}
        />
      }
    />
  );
};
